import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Heart, ShoppingCart, Loader2 } from 'lucide-react';
import { favoritesAPI } from '../../services/api';
import { getImageUrl, formatPrice } from '../../utils/helpers';

const UserFavorites = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const data = await favoritesAPI.getAll();
        setProducts(data);
      } catch {
        setProducts([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleRemove = async (productId) => {
    try {
      await favoritesAPI.toggle(productId);
      setProducts(prev => prev.filter(p => (p._id || p.id) !== productId));
    } catch { /* silent */ }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  if (products.length === 0) {
    return (
      <div data-testid="favorites-empty">
        <h1 className="text-2xl font-bold mb-6">Mis Favoritos</h1>
        <Card className="p-12 text-center">
          <Heart className="w-16 h-16 mx-auto text-gray-300 mb-4" />
          <h2 className="text-xl font-bold mb-2">No tienes favoritos</h2>
          <p className="text-gray-500 mb-6">Guarda productos que te gusten para verlos despues.</p>
          <Button onClick={() => navigate('/')} className="bg-orange-500 hover:bg-orange-600 text-white">
            Explorar productos
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div data-testid="favorites-page">
      <h1 className="text-2xl font-bold mb-6">Mis Favoritos</h1>
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        {products.map(product => {
          const pid = product._id || product.id;
          return (
            <Card key={pid} className="overflow-hidden hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate(`/product/${pid}`)}>
              <div className="relative">
                <img
                  src={getImageUrl(product.images?.[0] || product.image)}
                  alt={product.title}
                  className="w-full h-44 object-cover"
                  onError={e => { e.target.src = 'https://via.placeholder.com/300x200'; }}
                />
                <button
                  onClick={(e) => { e.stopPropagation(); handleRemove(pid); }}
                  className="absolute top-2 right-2 w-8 h-8 bg-white/90 rounded-full flex items-center justify-center shadow hover:bg-red-50 transition-colors"
                  data-testid={`remove-fav-${pid}`}
                >
                  <Heart className="w-4 h-4 fill-red-500 text-red-500" />
                </button>
              </div>
              <div className="p-3">
                <h3 className="text-sm text-gray-700 line-clamp-2 mb-1">{product.title}</h3>
                <p className="text-xl font-bold text-gray-900">{formatPrice(product.price)}</p>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default UserFavorites;
